import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import type { CreateConsentBody } from '@/consent/types/consent-api';

/**
 * POST /api/Consent/CreateConsent → string (new consent record GUID).
 *
 * Contract: Scrips.Patient `ConsentController.CreateConsent` /
 * `CreateConsentRequest` (PR #882). Creates a DRAFT consent — signatures are
 * applied separately via `useSignConsent` (BRD UC-CONSENT-01 main flow:
 * pick template → create draft → render → sign → activate).
 *
 * Date rule: `effectivePeriodStart` / `effectivePeriodEnd` ship no-Z
 * (`formatLocalDatetimeForWire`) — the .NET binder rejects the `Z` marker.
 * Add-mode dates start null (never pre-populated) per the behavioral contract.
 *
 * Cache: invalidates the patient's consent list so the chart Consents surface
 * refetches once the draft exists.
 */
export function useCreateConsent() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateConsentBody>({
    mutationFn: (body) =>
      post<string>('/api/Consent/CreateConsent', {
        ...body,
        effectivePeriodStart: body.effectivePeriodStart
          ? formatLocalDatetimeForWire(body.effectivePeriodStart)
          : null,
        effectivePeriodEnd: body.effectivePeriodEnd
          ? formatLocalDatetimeForWire(body.effectivePeriodEnd)
          : null,
      }),
    onSuccess: (_id, { patientId }) => {
      if (patientId) invalidateIfNotInFlight(qc, ['patient-consents', patientId]);
    },
  });
}
